<template>
   <Form as="div" ref="Form">
    <h3 v-html="title" />
    <p data-test-call-me-booking-date-description v-html="description" />
    <div class="control push-down">
      <label data-test-call-me-booking-date-label-date v-html="dateLabel" for="BookingDate" />
      <Field 
        name="BookingDate"
        id="BookingDate"
        :model-value="dateSelected"
        :rules="{ required: dateSelected === null }"
        :label="'Dato'"
      >
        <date-picker
          id="BookingDate"
          name="BookingDate"
          class="datepicker"
          data-vv-as="Dato"
          :min-date="currentDate"
          :max-date="maxDate"
          :days-disabled="timeSlotsMissingDates"
          @date-changed="dateChanged"
        />
      </Field>
      <ValidationError name="BookingDate" />
    </div>
    <div v-if="dateSelected !== null" class="control push-down">
      <label data-test-call-me-booking-date-label-time v-html="timeLabel" />
      <Field
        name="BookingTime"
        :model-value="timeSelected"
        :rules="{ required: true }"
        :label="'Tid'"
      >
        <VueSelect
          id="BookingTime"
          name="BookingTime"
          v-model="timeSelected"
          data-vv-as="Tid"
          :options="timeSlots"
          @update:model-value="timeSlotIsAvailable = true"
        />
      </Field>
      <ValidationError name="BookingTime" />
    </div>
    <div class="control push-down" v-if="!timeSlotIsAvailable">
      <info-box
        :arrow-show="false"
        info-box-class="info-box-warning-alt"
        :text="timeslotValidationError" 
      />
    </div>
    <div class="control">
      <button
        class="button large"
        data-test-callme-button
        @click="emitBookingDate"
        v-html="buttonLabel"
      />
    </div>
   </Form>
</template>

<script lang="ts">
import {
  CallMeBookingDateSalesforceData,
  Choice
} from './types/CallMe';
import DatePicker from '@/components/UI/Pickadate.vue';
import ValidationError from '@/components/UI/ValidationError.vue';
import RequestService from '@/common/requestService';
import { defineComponent } from 'vue';
import { Form, ValidationResult } from 'vee-validate';
import { SalesForceOpeningDays, OpeningDay } from './types/CallMe';
import Utility from '../../../common/UtilityFunctions';
import InfoBox from '../../UI/InfoBox.vue';

export default defineComponent({
  name: 'CallMeBookingDateSalesforce',
  components: {
    ValidationError,
    DatePicker,
    InfoBox
  },
  props: {
    title: {
      type: String,
      default() {
        return 'Vælg tidspunkt for opkald';
      }
    },
    description: {
      type: String,
      default() {
        return '<p>Vælg en dato og et tidspunkt, hvor du vil ringes op.</p>';
      }
    },
    dateLabel: {
      type: String,
      default() {
        return 'Dato';
      }
    },
    timeLabel: {
      type: String,
      default() {
        return 'Jeg vil gerne ringes op imellem...';
      }
    },
    buttonLabel: {
      type: String,
      default() {
        return 'Næste';
      }
    },
    timeslotValidationError: {
      type: String,
      default() {
        return 'Det valgte tidspunkt er desværre ikke længere ledigt.';
      }
    }
  },
  data(): CallMeBookingDateSalesforceData {
    return {
      currentDate: new Date(),
      dateSelected: null,
      timeSelected: null,
      timeSlotsSalesforce: null,
      timeSlotIsAvailable: true,
      timeSlotsMissingDates: [],
    };
  },
  mounted() {
    this.getTimeSlots();
  },
  computed: {
    timeSlots(): Choice[] | null {
      return this.makeTimeSlots(this.dateSelected);
    },
    maxDate(): Date {
      if (this.timeSlotsSalesforce === null) {
        return new Date();
      }
      const lastDay = this.timeSlotsSalesforce.dates[this.timeSlotsSalesforce.dates.length - 1];
      return new Date(lastDay.timeSlotsDate);
    }
  },
  methods: {
    async emitBookingDate(): Promise<void> {
      await this.getTimeSlots();

      const availableSlots = this.makeTimeSlots(this.dateSelected);
      const selectedTimeStillAvailable = availableSlots?.some(
        (slot) => slot.value === this.timeSelected?.value
      );

      (this.$refs.Form as typeof Form).validate().then((result: ValidationResult) => {
        if (!result.valid) return;
        if (!selectedTimeStillAvailable) {
          this.timeSlotIsAvailable = false;
          return;
        }
        const dateAndTime = {
          date: this.dateSelected,
          time: this.timeSelected
        };
        this.$emit('booking-date', dateAndTime);
      });
    },
    async getTimeSlots(): Promise<void> {
      await RequestService.get<SalesForceOpeningDays>(Utility.constants().API.salesforce_timeslots_get)
        .then((response: { data: SalesForceOpeningDays; }) => {
          this.timeSlotsSalesforce = response.data;
          this.timeSlotsMissingDates = this.getMissingDates(this.timeSlotsSalesforce);
        })
        .catch((err: any) => Utility.logError(err, this));
    },
    makeTimeSlots(day: Date | null): Choice[] | null {
      if (!this.timeSlotsSalesforce?.dates) return [];
      if(day === null) return [];

      const dateString = Utility.formatDateString(day);

      const openingDay = this.timeSlotsSalesforce.dates.find((day) => day.timeSlotsDate === dateString);

      if (openingDay && openingDay.timeSlots.length > 0)
        return openingDay.timeSlots.map((slot) => ({
          label: slot.timeInterval,
          value: slot.contactHoursStart.toString(),
        }));

      return [];
    },
    getMissingDates(timeSlotsData: SalesForceOpeningDays): string[] {
      let missingDates: string[] = [];
      if (!timeSlotsData) {
        return missingDates;
      }
      const dates: OpeningDay[] = timeSlotsData.dates;
      let startDate = new Date(dates[0].timeSlotsDate);
      const endDate = new Date(dates[dates.length - 1].timeSlotsDate);

      while (startDate <= new Date(endDate)) {
        const dateString = Utility.formatDateString(startDate);
        if (!dates.find((date) => date.timeSlotsDate === dateString)) {
          missingDates.push(dateString);
        }
        startDate.setDate(startDate.getDate() + 1);
      }

      return missingDates;
    },
    dateChanged(param: Date) {
      this.timeSlotIsAvailable = true;
      this.timeSelected = null;
      this.dateSelected = param;
    },
  }
});
</script>
